'use client';

import { useScroll, useTransform, motion, MotionValue } from 'motion/react';
import React, { useRef, forwardRef } from 'react';

interface SectionProps {
  scrollYProgress: MotionValue<number>;
}

const Section1: React.FC<SectionProps> = ({ scrollYProgress }) => {
  const scale = useTransform(scrollYProgress, [0, 0.33], [1, 0.8]);
  const rotate = useTransform(scrollYProgress, [0, 0.33], [0, -5]);
  return (
    <motion.section
      style={{ 
        scale, 
        rotate,
        backgroundImage: 'url(/photo/game.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      className='sticky font-semibold top-0 h-screen flex flex-col items-center justify-center text-white'
    >
      <div className='absolute bottom-0 left-0 right-0 top-0 bg-black/40'></div>

      <div className="relative z-10 text-center px-4">
        <h1 className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl px-8 font-bold text-center tracking-tight leading-[120%] mb-8' style={{ fontFamily: 'Vixa-Regular, sans-serif' }}>
          Game Zone 
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-[#F1EFE0] opacity-90">
          Discover my gaming world and projects
        </p>
      </div>
    </motion.section>
  );
};

const Section2: React.FC<SectionProps> = ({ scrollYProgress }) => {
  const scale = useTransform(scrollYProgress, [0, 0.33, 0.66], [0.8, 1, 0.8]);
  const rotate = useTransform(scrollYProgress, [0, 0.33, 0.66], [5, 0, -5]);

  return (
    <motion.section
      style={{ 
        scale, 
        rotate,
        backgroundImage: 'url(/photo/wangzhe.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      className='sticky top-0 h-screen text-white flex flex-col items-center justify-center'
    >
      <div className='absolute bottom-0 left-0 right-0 top-0 bg-black/50'></div>
      <article className='container mx-auto relative z-10 flex flex-col justify-center h-full px-4 sm:px-6 lg:px-8'>
        <h1 className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[100%] py-10 font-bold tracking-tight text-[#F1EFE0]' style={{ fontFamily: 'Vixa-Regular, sans-serif' }}>
          Honor of Kings <br /> Strategic Gameplay
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-[#F1EFE0] opacity-90 max-w-3xl">
          Master of tactical team coordination and strategic decision-making in competitive MOBA gaming.
        </p>
      </article>
    </motion.section>
  );
};

const Section3: React.FC<SectionProps> = ({ scrollYProgress }) => {
  const scale = useTransform(scrollYProgress, [0.33, 0.66, 1], [0.8, 1, 1]);
  const rotate = useTransform(scrollYProgress, [0.33, 0.66, 1], [5, 0, 0]);

  return (
    <motion.section
      style={{ 
        scale, 
        rotate,
        backgroundImage: 'url(/photo/pubg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
      className='relative h-screen text-white flex flex-col items-center justify-center mb-0'
    >
      <div className='absolute bottom-0 left-0 right-0 top-0 bg-black/50'></div>
      <article className='container mx-auto relative z-10 flex flex-col justify-center h-full px-4 sm:px-6 lg:px-8'>
        <h1 className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[100%] py-10 font-bold tracking-tight text-[#F1EFE0]' style={{ fontFamily: 'Vixa-Regular, sans-serif' }}>
          PUBG Veteran <br /> Battle Royale Master
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl text-[#F1EFE0] opacity-90 max-w-3xl">
          Have been playing PUBG since 2018, S1 season veteran. Every season is an ace. KD: 7.0.
        </p>
      </article>
    </motion.section>
  );
};

const Component = forwardRef<HTMLElement>((props, ref) => {
  const container = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start start', 'end end'],
  });

  return (
    <>
      <main ref={container} className='relative h-[300vh] bg-black'>
        <Section1 scrollYProgress={scrollYProgress} />
        <Section2 scrollYProgress={scrollYProgress} />
        <Section3 scrollYProgress={scrollYProgress} />
      </main>
    </>
  );
});

Component.displayName = 'Component';

export default Component;